import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button, softBadgeColors } from '@structural-climbing/ui'
import { disciplineForSection, gradeDisplay } from '@structural-climbing/validation'
import { SurfaceCard } from '../../design/SurfaceCard'
import { useGymGradeConfig } from '../gym/useGymGradeConfig'
import type { GymRoute } from '../routes/useGymRoutes'

/** How many easy routes the list suggests. */
export const STARTER_COUNT = 3

/** The easiest routes not topped yet, lowest grade first. */
export function starterRoutes(routes: GymRoute[]): GymRoute[] {
  return routes
    .filter((r) => !r.has_ascent)
    .sort((a, b) => a.grade - b.grade)
    .slice(0, STARTER_COUNT)
}

interface Props {
  routes: GymRoute[]
  apiUrl: string
  /** The wall, for "All". */
  wallTo: string
  /** Opens the route's own sheet, where it can be logged. */
  onPick: (route: GymRoute) => void
}

/**
 * "Bune de început": a first top on a 1 builds the habit, so someone with no
 * tops yet is shown the lowest grades on the wall rather than the newest.
 */
export function StarterRoutes({ routes, apiUrl, wallTo, onPick }: Props) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const list = starterRoutes(routes)
  if (list.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2 px-1">
        <p className="eyebrow text-foreground/40">{t('session.startersTitle')}</p>
        <Link to={wallTo} className="text-xs font-semibold text-[var(--gym-primary)]">
          {t('session.startersAll')} →
        </Link>
      </div>
      <SurfaceCard className="flex flex-col divide-y divide-[var(--gym-line)] overflow-hidden p-0">
        {list.map((route) => {
          const grade = gradeDisplay(
            gradeConfig,
            route.grade,
            disciplineForSection(route.section?.discipline),
          )
          const badge = softBadgeColors(grade.color)
          return (
            <Button
              key={route.id}
              variant="light"
              disableRipple
              data-testid="starter-route"
              className="h-auto w-full justify-start gap-3 rounded-none px-4 py-3"
              onPress={() => onPick(route)}
            >
              {route.image_r2_key ? (
                <img
                  src={`${apiUrl}/climbing-routes/${route.id}/image?size=thumb`}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded-md object-cover"
                  loading="lazy"
                />
              ) : (
                <span
                  aria-hidden
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-content2 text-foreground/30"
                >
                  ⛰
                </span>
              )}
              <span
                className="h-4 w-4 shrink-0 rounded-full border border-foreground/10"
                style={{ backgroundColor: route.color ?? '#9ca3af' }}
              />
              <span
                className="rounded px-2 py-0.5 text-sm font-semibold"
                style={{ backgroundColor: badge.bg, color: badge.fg }}
              >
                {grade.label}
              </span>
              {route.section?.name && (
                <span className="truncate text-sm text-foreground/55">{route.section.name}</span>
              )}
            </Button>
          )
        })}
      </SurfaceCard>
    </div>
  )
}
